<template>
  <div id="app home">
    <div v-if="!loaded" class="loader">
      <div class="loading loading-lg"></div>
    </div>

    <template v-else>
      <options></options>
      <div class="toast text-small" v-if="exchangeId.indexOf('Fic_In_A_Box') > -1">
        Fic In A Box displays random visual easter eggs in the app. To disable them, use the toggle
        in Filters & Options.
      </div>
      <pagination :options="options" :pagination="pagination" @page="page"></pagination>

      <div v-if="updating" class="loader">
        <div class="loading loading-lg"></div>
      </div>

      <div class="toast toast-warning" v-if="!updating && !processing && isExchangeOpen">
        Prompt bookmarking is disabled until after signups are closed. Someone must re-scrape again
        at that time.
      </div>

      <div class="toast toast-warning" v-if="processing && exchangeId">
        The <strong>last successful scrape is being shown while a new
          scrape
          is in-progress</strong>. Caveat lector. Prompt bookmarking is disabled until after signups are closed. Someone
        must re-scrape
        again
        at that time.
      </div>


      <component :is="groupComponent" :processing="false" :updating="updating" :filtered="filtered">
      </component>

      <div class="mt-2" v-if="!updating && allOptions.length && !filtered.length">
        Either nothing matches your filters or this exchange can't be grouped using the current
        Group By filter ({{ options.groupBy }}) you're on ): Change the Group By option using the
        dropdown above, or you could try searching
        <router-link :to="`/${exchangeId}/all`">All Requests</router-link>
        instead.
      </div>

      <div class="mt-2" v-if="!updating && !allOptions.length">
        Looks like prompts for this exchange can't be grouped using the current Group By filter ({{
          options.groupBy
        }}) you're on! Change the Group By option using the dropdown above, or you can view and
        search all prompts on the
        <router-link :to="`/${exchangeId}/all`">All Requests tab</router-link>
        instead.
      </div>

      <div v-if="processing">
        <div v-if="status.status == 'processing'">
          The scraper is currently working hard to process this challenge. Come back in 15 minutes
          or so, though this could be much longer for large exchanges or when AO3 is experiencing slowness. If it has
          been over 2 hours since a successful scrape, comment on the bug
          report page using the link at the top of the page!
        </div>
        <div v-if="status.status == 'busy'">
          The scraper is currently working hard to process another challenge,
          {{ status.currentlyScraping }}. Come back in 15 minutes
          or so, though this could be much longer for large exchanges or when AO3 is experiencing slowness. If it has
          been over 2 hours, comment on the bug
          report page using the link at the top of the page!
        </div>
        <div v-if="status.status == 'unparseable'">
          This exchange isn't parseable into groups yet ): That said, you might still be able use
          the
          <router-link :to="{ path: `/${exchangeId}/all` }">All Requests</router-link>
          tab to search requests.

          <p class="mt-2">
            The app can only autoparse exchanges that have public requests summaries and which do
            not require the user to be logged in. The server says this about this particular
            exchange: {{ status.msg }}...
          </p>
        </div>
      </div>

      <pagination :options="options" :pagination="pagination" @page="page"></pagination>
    </template>

    <img src="../assets/likeztobatstuff.png" alt="Razia's cat is here to bat stuff!" class="cat-easter-egg" ref="cat" />
    <img src="../assets/mintz22.png" alt="Razia's cat is here to bat stuff!" class="cat-easter-egg" ref="catt" />
    <img src="../assets/panicmintz.png" alt="Razia's cat is here to bat stuff!" class="cat-easter-egg" ref="catpanic" />
  </div>


</template>

<script>
import Options from "../components/options.vue";
import FandomTable from "../components/fandomTable.vue";
import GroupedTable from "../components/groupedTable.vue";
import Pagination from "../components/pagination.vue";
import utils from "../components/utils.js";
import tableResizer from "../directives/tableResizer";
// third party
import { map, isEqual, random, uniq, filter, slice, sortBy, each, includes, find } from "lodash-es";
import { mapGetters } from "vuex";
import axios from "axios";
import data from "../store/data";

export default {
  name: "home",
  components: {
    Pagination,
    Options,
    FandomTable,
    GroupedTable
  },
  beforeMount() {
    this.$store.commit("setPrompts", {});
    this.$store.commit("setFandoms", []);
    this.$store.commit("setHasPrompts", []);
    this.$store.commit("setAggregatedPrompts", {});
    this.load();
  },
  created() {
    this.loadFandomBookmarksAndPromptmarks();
  },
  mounted() {
    this.showFiabCat();
  },
  directives: {
    tableResizer
  },
  data() {
    return {
      allOptions: [],
      scraperRes: undefined,
      scraperLoading: false,
      processing: false,
      status: {},
      open: true,
      showHelp: false,
      down: {},
      mods: false,
      filtered: [],
      updating: true,
      sticky: false,
      promptStates: {},
      hasFreeformPrompts: [],
      pagination: {
        first: 0,
        last: 100,
        size: 100,
        total: 0
      },
      groupComponent: FandomTable,
      controller: undefined
    };
  },
  computed: {
    ...mapGetters([
      "exchangeId",
      "isExchangeOpen",
      "bookmarks",
      "freeformmarks",
      "categories",
      "characters",
      "fandoms",
      "hasPrompts",
      "letters",
      "loadAll",
      "loaded",
      "options",
      "promptmarks",
      "freeformmarks",
      "relationshipmarks",
      "prompts",
      "user"
    ]),
    marksCount() {
      let count = 0;
      if (this.bookmarks && this.bookmarks.length) {
        count += this.bookmarks.length;
      }
      if (this.promptmarks && this.promptmarks.length) {
        count += this.promptmarks.length;
      }

      if (this.freeformmarks && this.freeformmarks.length) {
        count += this.freeformmarks.length;
      }

      if (this.relationshipmarks && this.relationshipmarks.length) {
        count += this.relationshipmarks.length;
      }

      return count;
    },
  },
  watch: {
    async $route(to) {
      if (to.name === "grouped") {
        await this.redirectExchangeId(this.$route);
      }
    },
    exchangeId(val, oldVal) {
      if (val == oldVal) return;

      this.$store.commit("setPrompts", {});
      this.$store.commit("setAggregatedPrompts", {});
      this.$store.commit("setFandoms", []);
      this.$store.commit("setTimestamp", null);
      this.$store.commit("setHasPrompts", []);

      const newOptions = this.$store.state.options;
      this.$store.commit("setOptions", newOptions);

      this.load();
      this.showFiabCat();
    },
    allOptions(newVal, oldVal) {
      if (isEqual(newVal, oldVal)) return;
      this.pagination.first = 0;
      this.pagination.last = this.pagination.size;
      this.updateFilter();
    },
    // Deep watching doesn't work
    "options.filter.term": function () {
      this.updateFilter(true);
    },
    "options.filter.category": function () {
      this.updateFilter(true);
    },
    "options.onlyBookmarks": function () {
      this.updateFilter(true);
    },
    "options.onlyPrompts": function () {
      this.updateFilter(true);
    },
    "options.groupBy": async function (newVal, oldVal) {
      if (newVal == oldVal) return;

      if (this.$route.query && this.$route.query.groupby != newVal) {
        this.$router.replace({
          path: this.$route.path,
          query: {
            groupby: newVal
          }
        });
      }

      this.$store.commit("setPrompts", {});
      await this.loadPrompts();
    },
    "options.loadAll": async function (val) {
      if (val && !this.loadAll.characters) {
        this.loadTagsetCharacters();
      } else if (!val) {
        this.pagination.last = this.pagination.size;
      }
      this.updateFilter();
    },
    "options.hideCharacters": async function (val) {
      // If the characters aren't loaded AND this is toggled on, load stuff because our optimisations are always
      // sub-par: we can't predict how many fandoms don't
      // have requests anyway

      if (val || this.loadAll.characters) return;
      this.loadTagsetCharacters();
    },
    "options.disableEasterEggs": function () {
      this.showFiabCat();
    }
  },
  methods: {
    ...utils,
    async load() {
      await this.redirectExchangeId(this.$route);

      this.status = {};
      this.hasFreeformPrompts = [];
      this.scraperRes = undefined;
      this.scraperLoading = false;
      this.processing = false;
      this.$store.commit("setCharsLoaded", false);
      this.$store.commit("setDbLoaded", false);

      const exchangeId = this.$route.params.exchangeId;
      this.$store.commit("setExchangeId", this.$route.params.exchangeId);

      // exchange metadata
      axios.get(`https://autoao3.herokuapp.com/db/exchanges/${exchangeId}/status`).then((res) => {
        if (res.data && res.data.status != "open" && res.data.status != "complete") {
          this.processing = true;
          this.status = res.data;
        }
        this.$store.commit("setTimestamp", res.data.scraped);

        this.$store.commit("setIsExchangeOpen", res.data && (res.data.status == "open" || res.data.status == "processing"));
      });

      await this.loadPrompts();
      this.loadFandomBookmarksAndPromptmarks();
    },
    async loadPrompts() {
      if (!this.options.groupBy) {
        let newOptions = this.options;
        newOptions.groupBy = 'fandom';
        this.$store.commit("setOptions", newOptions);
      }
      switch (this.options.groupBy.toLowerCase()) {
        case "relationship":
        case "freeform":
        case "character":
          await this.loadGroupedPrompts();
          break;
        case "fandom":
        default:
          await this.loadFandoms();
          break;
      }

      if (!this.loaded) {
        this.$store.commit("setDbLoaded", true);
      }
    },

    async loadGroupedPrompts() {
      this.updating = true;

      this.groupComponent = GroupedTable;

      if (this.controller) {
        this.controller.abort();
      }

      this.controller = new AbortController();
      if (!this.exchangeId) {
        this.allOptions = [];
        this.updating = false;
        return;
      }
      let tags;

      try {
        switch (this.options.groupBy.toLowerCase()) {
          case "freeform":
            tags = await data.getExchangeFreeforms(this.exchangeId, this.controller.signal);
            let requestedFreeforms = await data.getExchangeFreeforms(this.exchangeId, this.controller.signal, true);
            this.hasFreeformPrompts = map(requestedFreeforms, o => o.tag)
            break;
          case "relationship":
            tags = await data.getExchangeRelationships(this.exchangeId, this.controller.signal);
            break;
          case "character":
            tags = await data.getExchangeCharacters(this.exchangeId, this.controller.signal);
            break;
        }

        tags = sortBy(tags, [utils.removeArticlesCompare, tag => tag.tag.toLowerCase()]);
        this.allOptions = tags;
        this.updating = false;
      } catch (e) {
        this.allOptions = [];
      }

      // fuse = new Fuse(fandoms, options);
      // this.controller = undefined;
    },
    async loadFandoms() {
      this.groupComponent = FandomTable;

      //  Just grab all characters if we haven't already; damn the torpedoes
      if (!this.options.hideCharacters && !this.loadAll.characters) {
        this.loadTagsetCharacters();
      }

      await axios
        .get(`https://autoao3.herokuapp.com/db/exchanges/${this.exchangeId}/tagset/hasprompts`)
        .then(async (res) => {
          let data = res.data;

          if (this.exchangeId && this.exchangeId.toLowerCase() == ("auctionsasthreatened")) {
            let overflowReq = await axios
              .get(`https://autoao3.herokuapp.com/db/exchanges/AAT_Request_Overflow/tagset/hasprompts`);
            let overflow = overflowReq.data;

            // MERGE DATA A FOR AUCTION BOT
            for (let i = 0; i < data.length; i++) {
              let fandom = data[i];

              let overflowFandom = find(overflow, f => {
                return f.name == fandom.name;
              });

              if (!fandom.has_prompts && overflowFandom && overflowFandom.has_prompts) {
                data[i].has_prompts = true;
              }
            }

            this.$store.commit("setHasPrompts", data);

          } else if (this.exchangeId && this.exchangeId.toLowerCase() == ("aat_request_overflow")) {
            let overflowReq = await axios
              .get(`https://autoao3.herokuapp.com/db/exchanges/AuctionsAsThreatened/tagset/hasprompts`);
            let overflow = overflowReq.data;

            // MERGE DATA A FOR AUCTION BOT
            for (let i = 0; i < data.length; i++) {
              let fandom = data[i];

              let overflowFandom = find(overflow, f => {
                return f.name == fandom.name;
              });

              if (!fandom.has_prompts && overflowFandom && overflowFandom.has_prompts) {
                data[i].has_prompts = true;
              }
            }

            this.$store.commit("setHasPrompts", data);

          } else {
            this.$store.commit("setHasPrompts", res.data);
          }
        });

      data.getExchangeFandoms(this.exchangeId).then((result) => {
        //------- TODO: if not result.length, switch to using
        // derived non-tagset fandoms from `https://autoao3.herokuapp.com/db/fandoms/requested`)
        // and switch getting prompts from using google to heroku

        //-----------
        this.$store.commit("setDbLoaded", true);
        this.$store.commit("setFandoms", result);
        this.allOptions = result;
        let categories = [];
        each(result, (o) => {
          if (!o) {
            return;
          }
          each(o.categories, (cat) => {
            if (!cat.length) {
              return;
            }
            categories.push(cat);
          });
        });

        categories = sortBy(categories, (c) => c);
        this.$store.commit("setCategories", uniq(categories));
      });
    },
    async loadTagsetCharacters() {
      this.updating = true;
      this.$store.commit("setCharsLoaded", false);
      this.$store.commit("loadAllChars", false);

      await axios
        .get(`https://autoao3.herokuapp.com/db/exchange/${this.exchangeId}/tagset/characters`)
        .then((res) => {
          this.$store.commit("setCharacters", []);
          this.$store.commit("setCharacters", res.data);
          this.$store.commit("setCharsLoaded", true);
          this.$store.commit("loadAllChars", true);

          this.updating = false;
        });
    },
    updateFilter(resetPagination) {
      this.updating = true;

      if (resetPagination) {
        this.pagination.first = 0;
        this.pagination.last = this.pagination.size;
      }

      // Do not let pagination go below 0
      if (this.pagination.first < 0) {
        this.pagination.first = 0;
      }

      // When loadAll is turned on, always page all the way
      if (this.options.loadAll) {
        this.pagination.first = 0;
        this.pagination.last = this.fandoms.length;
      }

      // Remove tags that have no names
      const groupBy = this.options.groupBy.toLowerCase();
      const isFandomGroupBy = groupBy == "fandom";
      const isFreeformGroupBy = groupBy == "freeform";
      const isRelationshipGroupBy = groupBy == "relationship";

      let arr = this.allOptions;
      if (isFandomGroupBy) {
        arr = filter(arr, (f) => {
          return f.name ? f.name.length : f.length;
        });
      }

      // Case: no filters
      // Show all fandoms, slicing by pagination
      if (
        !this.options.onlyPrompts &&
        !this.options.filter.term.length &&
        !this.options.onlyBookmarks &&
        !this.options.filter.category.length
      ) {
        const sortOptions = isFandomGroupBy ? ["categories", utils.removeArticlesCompare] : [tag => tag.tag.toLowerCase()];

        this.filtered = slice(
          sortBy(arr, sortOptions),
          this.pagination.first,
          this.pagination.last
        );

        this.pagination.total = arr.length;

        setTimeout(() => {
          this.updating = false;
        }, 150);

        return;
      }

      // Case: apply filters
      if (
        isFandomGroupBy &&
        this.options.onlyPrompts &&
        this.hasPrompts &&
        this.hasPrompts.length
      ) {
        arr = filter(arr, (o) => {
          return this.hasPrompts[o.index] && this.hasPrompts[o.index].has_prompts;
        });
      } else if (isFreeformGroupBy && this.options.onlyPrompts) {
        arr = filter(arr, (o) => {
          return includes(this.hasFreeformPrompts, o.tag)
        })
      }

      if (this.options.onlyBookmarks) {
        if (isFandomGroupBy) {
          const bookmarkedFandoms = [];
          each(this.bookmarks, (b) => {
            bookmarkedFandoms.push(b.name);
          });

          arr = filter(arr, (o) => {
            return includes(bookmarkedFandoms, o.name);
          });
        } else if (isFreeformGroupBy) {
          const bookmarkedFreeforms = [];
          each(this.freeformmarks, (tag) => {
            bookmarkedFreeforms.push(tag);
          });

          arr = filter(arr, (o) => {
            return includes(bookmarkedFreeforms, o.tag);
          });
        } else if (isRelationshipGroupBy) {
          const bookmarkedRelationships = [];
          each(this.relationshipmarks, (tag) => {
            bookmarkedRelationships.push(tag);
          });

          arr = filter(arr, (o) => {
            return includes(bookmarkedRelationships, o.tag);
          });
        }
      }

      if (isFandomGroupBy && this.options.filter.category.length) {
        arr = filter(arr, (o) => {
          return includes(o.categories, this.options.filter.category);
        });
      }

      if (this.options.filter.term.length) {
        arr = filter(arr, (o) => {
          let tag = isFandomGroupBy ? o.name : o.tag;
          const name = tag
            .toLowerCase()
            .normalize("NFD")
            .replace(/[\u0300-\u036f]/g, "");
          return name.indexOf(this.options.filter.term.toLowerCase()) > -1;
        });
      }

      // Set pagination.last if the filtered array ends up being smaller
      // or if loadAll is turned on
      if (arr.length < this.pagination.last || this.options.loadAll) {
        this.pagination.last = arr.length;
      }

      // Slice search word and category filters by name without
      // regard for category
      if (
        (isFandomGroupBy && this.options.filter.term.length) ||
        this.options.filter.category.length
      ) {
        this.filtered = slice(
          sortBy(arr, [utils.removeArticlesCompare]),
          this.pagination.first,
          this.pagination.last
        );

        this.pagination.total = arr.length;

        setTimeout(() => {
          this.updating = false;
        }, 100);

        return;
      }

      // Otherwise, sort by category before slicing
      this.filtered = slice(
        sortBy(arr, ["categories", utils.removeArticlesCompare]),
        this.pagination.first,
        this.pagination.last
      );
      this.pagination.total = arr.length;

      setTimeout(() => {
        this.updating = false;
      }, 100);
    },
    async page(start) {
      this.pagination.last = start + this.pagination.size;
      this.pagination.first = start;
      this.updateFilter();
    },
    scrollToTop() {
      document.body.scrollTop = 0;
      document.documentElement.scrollTop = 0;
    },
    showFiabCat() {
      let cats = ["cat", "catt"];
      if (
        new Date("2024-11-17T00:59:00.000Z") < new Date() &&
        new Date() < new Date("2024-11-20T05:00:00.000Z")
      ) {
        cats = ["catpanic"];
      }

      for (let i = 0; i < cats.length; i++) {
        this.$refs[cats[i]].style.display = "none";
      }

      if (
        !this.exchangeId ||
        this.exchangeId.indexOf("Fic_In_A_Box") == -1
        || this.options.disableEasterEggs) {
        return;
      }

      let cat = cats[random(0, cats.length - 1)];
      let locations = ["right"]
      let location = locations[random(0, locations.length - 1)]

      // set the position
      this.$refs[cat].style.position = "absolute";
      this.$refs[cat].style.bottom = window.innerHeight * Math.random() + "px";
      this.$refs[cat].style[location] = "10px"

      this.$refs[cat].style.display = "block";
      this.$refs[cat].classList.toggle("flip");
    },
    process() {
      this.scraperLoading = true;
      axios
        .get(`https://autoao3.herokuapp.com/scrape/${this.exchangeId}`)
        .then((res) => {
          this.scraperRes = res;
          this.scraperLoading = false;
        })
        .catch((e) => {
          if (e.response) {
            this.scraperRes = e.response;
            this.scraperLoading = false;
            return;
          }
          this.scraperRes = {
            error: true,
            e: e,
            msg: "Something went wrong! Report a bug."
          };
          this.scraperLoading = false;
        });
    }
  }
};
</script>

<style lang="scss">
@import "../styles/app.scss";
@import "../styles/mobile.scss";

.text-small {
  font-size: 80%;
}
</style>
